import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Search, Download, ChevronLeft, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function UpstreamError({ detail }) {
  return (
    <div data-testid="upstream-error" style={{
      background: '#fef9ec', border: '1px solid #fde68a', borderRadius: 8,
      padding: '20px 24px', margin: '24px 0',
    }}>
      <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 6, fontSize: 14 }}>
        Upstream API Unavailable
      </div>
      <div style={{ color: '#78350f', fontSize: 13 }}>{detail}</div>
      <div style={{ color: '#92400e', fontSize: 12, marginTop: 8 }}>
        This page requires a lupnxtlaravel account. Contact your administrator.
      </div>
    </div>
  );
}

function StatusPill({ value }) {
  const active = String(value).toLowerCase() === 'active' || value === 1 || value === true;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600,
      background: active ? '#dcfce7' : '#f1f5f9',
      color: active ? '#15803d' : '#64748b',
    }}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function RatePill({ value }) {
  const v = Number(value || 0);
  const color = v >= 70 ? '#16a34a' : v >= 40 ? '#d97706' : '#64748b';
  return <span style={{ color, fontWeight: 600 }}>{v.toFixed(1)}%</span>;
}

function exportCSV(rows, filename) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const lines = [
    keys.join(','),
    ...rows.map(r => keys.map(k => {
      const v = r[k] ?? '';
      return String(v).includes(',') ? `"${v}"` : v;
    }).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function CoursesAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [upstreamError, setUpstreamError] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState(null);

  const PER_PAGE = 50;

  const fetchData = useCallback(async (pg = 1, q = '') => {
    setLoading(true);
    setError(null);
    setUpstreamError(null);
    try {
      const params = { page: pg, per_page: PER_PAGE };
      if (q) params.search = q;
      const res = await axios.get(`${API}/api/lup/courses`, { params, withCredentials: true });
      const data = res.data;
      if (Array.isArray(data)) {
        setRows(data);
        setMeta(null);
      } else if (data?.data) {
        setRows(data.data);
        setMeta(data.meta || null);
      } else {
        setRows([]);
      }
    } catch (e) {
      if (e.response?.status === 503) {
        setUpstreamError(e.response.data?.detail || 'Not authenticated with upstream API.');
      } else {
        setError(e.response?.data?.detail || 'Failed to load courses data.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(1, search); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchData(1, search);
  };

  const handlePage = (newPage) => {
    setPage(newPage);
    fetchData(newPage, search);
  };

  const COLUMNS = useMemo(() => {
    if (!rows.length) return [];
    const preferred = [
      { key: 'course_id',      label: 'Course ID' },
      { key: 'name',           label: 'Course Name', maxWidth: 280 },
      { key: 'solution_area',  label: 'Solution Area' },
      { key: 'course_duration', label: 'Duration' },
      { key: 'archived',       label: 'Status', render: v => <StatusPill value={!v} /> },
    ];
    const available = new Set(Object.keys(rows[0]));
    return preferred.filter(c => available.has(c.key));
  }, [rows]);

  const lastPage = meta?.last_page || (meta?.total ? Math.ceil(meta.total / PER_PAGE) : null);

  return (
    <div data-testid="courses-admin" style={{ padding: '0 16px' }}>
      <div style={{ padding: '20px 0 12px' }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>Courses Admin</h1>
        <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>
          View and manage all LMS courses via lupnxtlaravel.
        </p>
      </div>

      {upstreamError && <UpstreamError detail={upstreamError} />}

      {!upstreamError && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  data-testid="course-search"
                  type="text"
                  placeholder="Search course name…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{ height: 34, paddingLeft: 32, paddingRight: 12, fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 7, outline: 'none', width: 260 }}
                />
              </div>
              <button type="submit" style={{ height: 34, padding: '0 16px', fontSize: 13, fontWeight: 600, background: '#194449', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer' }}>
                Search
              </button>
            </form>
            <button
              data-testid="course-export-btn"
              onClick={() => exportCSV(rows, 'courses.csv')}
              disabled={!rows.length}
              style={{ height: 34, padding: '0 14px', fontSize: 13, fontWeight: 600, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Download size={14} /> Export CSV
            </button>
          </div>

          <div className="report-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table className="report-table" data-testid="courses-table">
                <thead>
                  <tr>
                    {COLUMNS.map(c => (
                      <th key={c.key} style={{ textAlign: c.align === 'right' ? 'right' : 'left' }}>{c.label}</th>
                    ))}
                    {!COLUMNS.length && <th>Loading columns…</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={COLUMNS.length || 1} style={{ textAlign: 'center', color: '#94a3b8', padding: '32px 0' }}>Loading…</td></tr>
                  ) : error ? (
                    <tr><td colSpan={COLUMNS.length || 1} style={{ textAlign: 'center', color: '#dc2626', padding: '24px 0', fontSize: 13 }}>{error}</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={COLUMNS.length || 1} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0' }}>No courses found.</td></tr>
                  ) : (
                    rows.map((row, i) => (
                      <tr key={row.id ?? i} data-testid={`course-row-${row.id ?? i}`}>
                        {COLUMNS.map(c => (
                          <td key={c.key} style={{ textAlign: c.align === 'right' ? 'right' : 'left', fontSize: 13, maxWidth: c.maxWidth || 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.render ? c.render(row[c.key]) : (row[c.key] ?? '—')}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {lastPage && lastPage > 1 && (
            <div data-testid="courses-pagination" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13 }}>
              <span style={{ color: '#64748b' }}>
                Page {page} of {lastPage}{meta?.total ? ` (${meta.total.toLocaleString()} total)` : ''}
              </span>
              <button onClick={() => handlePage(page - 1)} disabled={page <= 1} style={{ width: 32, height: 32, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: page > 1 ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ChevronLeft size={14} />
              </button>
              <button onClick={() => handlePage(page + 1)} disabled={page >= lastPage} style={{ width: 32, height: 32, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: page < lastPage ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
